import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { AfterViewInit, Component, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { formatEnumName } from 'src/app/_helpers/enum-helper';
import { MediaTypeEnum } from 'src/app/enums/mediaType.enum';
import { PhaseEnum } from 'src/app/enums/phase.enum';
import { AnalyticalMethodDialogComponent } from 'src/app/modals/analytical-method-dialog/analytical-method-dialog.component';
import { SampleMediaDialogComponent } from 'src/app/modals/sample-media-dialog/sample-media-dialog.component';
import { Lookup } from 'src/app/models/lookup.model';
import { SampleMedia } from 'src/app/models/sampleMedia.model';
import { AnalyticalMethodService } from 'src/app/services/analytical-method.service';
import { SampleMediaService } from 'src/app/services/sample-media.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import nameOf from 'src/app/shared/form/nameOf.helper';

@Component({
  standalone: false,
  selector: 'app-sample-media-details-popup',
  templateUrl: './sample-media-details-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sample-media-details-popup.component.scss']
})
export class SampleMediaDetailsPopupComponent implements AfterViewInit {
  sampleMediaDetails: SampleMedia;
  displayedColumns = [nameOf<SampleMedia>(x => x.name), 'actions'];
  dataSource = new MatTableDataSource<Lookup>();
  isLoading = false;
  mediaFields = [
    { key: nameOf<SampleMedia>(x => x.id), label: 'Media ID' },
    { key: nameOf<SampleMedia>(x => x.poreSizeMicrons), label: 'Pore Size (Microns)' },
    { key: nameOf<SampleMedia>(x => x.name), label: 'Media Name' },
    { key: nameOf<SampleMedia>(x => x.diameterMM), label: 'Diameter (mm)' },
    { key: nameOf<SampleMedia>(x => x.mediaType), label: 'Media Type', enum: MediaTypeEnum },
    { key: nameOf<SampleMedia>(x => x.sorbentMaterial), label: 'Sorbent Material' },
    { key: nameOf<SampleMedia>(x => x.manufacturer), label: 'Manufacturer' },
    { key: nameOf<SampleMedia>(x => x.phase), label: 'Phase', enum: PhaseEnum },
    { key: nameOf<SampleMedia>(x => x.recommendedFlowRateSccm), label: 'Recommended Flow Rate' },
    { key: nameOf<SampleMedia>(x => x.usageNotes), label: 'Usage Notes' }
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    @Inject(FLYOUT_DATA) public data: number,
    private sampleMediaService: SampleMediaService,
    private analyticalMethodService: AnalyticalMethodService,
    private toastService: ToastService,
    private translateService: TranslateService,
    protected dialog: MatDialog,
  ) {
    if (data && !isNaN(data)) {
      this.getSampleMediaDetails();
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  getSampleMediaDetails() {
    this.isLoading = true;
    this.sampleMediaService.getById(this.data).subscribe(
      (response) => {
        this.sampleMediaDetails = response;
        this.getAnalyticalMethods();
      },
      (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      });
  }

  getAnalyticalMethods() {
    if (!this.sampleMediaDetails.analyticalMethodIds.length || !this.sampleMediaDetails.analyticalMethodIds.length) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    const filter = JSON.stringify({ id: this.sampleMediaDetails.analyticalMethodIds.join(',') });
    this.analyticalMethodService.getAll(filter).subscribe({
      next: (response) => {
        this.dataSource.data = response;
        this.isLoading = false;
      },
      error: (error) => {
        this.toastService.showError(error);
        this.dataSource.data = [];
        this.isLoading = false;
      }
    });
  }

  getEnumDisplayValue(enumType: Record<number, string>, value: unknown): string {
    if (typeof value !== 'number') {
      return '-';
    }


    const enumKey = enumType[value];
    return enumKey !== undefined ? this.translateService.getDynamicTranslation(formatEnumName(enumKey)) : '-';
  }

  openSampleMediaDialog(entityId?: string) {
    const dialogRef = this.dialog.open(SampleMediaDialogComponent, {
      data: {
        id: entityId ?? ''
      },
      width: '760px',
      disableClose: true
    });
    this.closeDialog(dialogRef);
  }

  openAnalyticalDialog(entityId?: string) {
    const dialogRef = this.dialog.open(AnalyticalMethodDialogComponent, {
      data: {
        id: entityId ?? ''
      },
      width: '760px',
      disableClose: true
    });
    this.closeDialog(dialogRef);
  }

  closeDialog(dialogRef) {
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) this.getSampleMediaDetails();
    });
  }

  public trackByIndex = trackByIndex;
}
